import React, { useState, useEffect, useCallback } from "react"
import { useLocation } from "react-router-dom"
import { Container, Table, Alert, Pagination, PaginationItem, PaginationLink } from "reactstrap"
import { useMutation, useQuery } from "@tanstack/react-query"
import Swal from "sweetalert2"
import { debounce } from "lodash"

import storage from "services/storage"
import { authUserKey } from "config/API"
import { getBranches, deleteBranch } from "services/branch-offices"
import { Loading } from "components"
import { BranchesItemList, CreateModalForm, EditModalForm } from "components/branches"
import ListHeader from "ui-componets/list-header"
import Shortcuts from "components/Shortcuts"

const BranchesList = () => {
  document.title = "Lista de sucursales | Punto de venta"

  const Location = useLocation()

  const [filters, setFilters] = useState("")
  const [searchTxt, setSearchTxt] = useState(new URLSearchParams(Location.search).get("search") ?? "")
  const [modalCreate, setmodalCreate] = useState(false)
  const [modalEdit, setmodalEdit] = useState(false)
  const [branchId, setbranchId] = useState(null)
  const [showAlertSuccess, setShowAlertSuccess] = useState(false)
  const [msgSuccess, setMsgSuccess] = useState("")
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener("mouseenter", Swal.stopTimer)
      toast.addEventListener("mouseleave", Swal.resumeTimer)
    },
  })
  const styleCenterTable = { textAlign: "center", verticalAlign: "middle" }

  const branches = useQuery(["branches", filters, filters.toString()], () => getBranches(filters))
  const branchDeleted = useMutation((id) => deleteBranch(id))
  const isLoading = branches.isLoading || branchDeleted.isLoading
  const permissions = storage.get(authUserKey).user_permissions || []
  const canEdit = permissions.some((e) => e.codename === "change_branchoffice")
  const canDelete = permissions.some((e) => e.codename === "delete_branchoffice")
  const canAdd = permissions.some((e) => e.codename === "add_branchoffice")

  useEffect(() => {
    if (showAlertSuccess) {
      Toast.fire({
        icon: "success",
        title: msgSuccess,
        didOpen: () => {
          branches.refetch()
          setShowAlertSuccess(false)
        },
        confirmButtonText: "Aceptar",
        confirmButtonColor: "green",
      })
    }
  }, [showAlertSuccess])

  useEffect(() => {
    setFilters(Location.search)
  }, [])

  const toggleModalCreate = () => {
    setmodalCreate(!modalCreate)
  }

  const toggleModalEdit = () => {
    if (modalEdit) {
      setbranchId("")
    }
    setmodalEdit(!modalEdit)
  }

  const openEditModal = (branchId) => {
    setbranchId(branchId)
    toggleModalEdit()
  }

  const openDeleteModal = (row) => {
    Swal.fire({
      title: `¿Está seguro que desea eliminar la sucursal: <b>${row.name}</b>?`,
      text: "Este cambio no se podrá revertir",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Aceptar",
      cancelButtonText: "Cancelar",
    }).then((result) => {
      if (result.isConfirmed) {
        branchDeleted.mutateAsync(row?.id).then(() => {
          branches.refetch()
          Toast.fire("!Eliminada!", `La sucursal <b>${row.name}</b> ha sido eliminado correctamente`, "success")
        })
      }
    })
  }

  const handleSearch = (pageParams) => {
    const params = new URLSearchParams(filters)
    if (pageParams) {
      const newParams = new URLSearchParams(pageParams)
      window.history.replaceState({}, null, `${Location.pathname}?${newParams}`)
      setFilters(newParams)
    } else if (searchTxt) {
      params.set("search", searchTxt)
      params.delete("offset")
      params.delete("per_page")
      window.history.replaceState({}, null, `${Location.pathname}?${params}`)
      setFilters(params)
    } else {
      if (params.get("search")) {
        params.delete("offset")
        params.delete("per_page")
        params.delete("search")
        window.history.replaceState({}, null, `${Location.pathname}?${params}`)
        setFilters(params)
      }
    }
  }

  const debouncedSearch = useCallback(
    debounce((text) => {
      const params = new URLSearchParams()
      params.set("search", text)
      params.delete("offset")
      params.delete("per_page")
      window.history.replaceState({}, null, `${Location.pathname}?${params}`)
      setFilters(params)
    }, 300),
    []
  )

  function handleChangeSearch(event) {
    setSearchTxt(event.target.value)
    debouncedSearch(event.target.value)
  }

  return !permissions.some((e) => e.codename === "view_branchoffice") ? (
    <div className="page-content">
      <div className="text-center">
        <h1>No autorizado para ver esta pagina</h1>
      </div>
    </div>
  ) : (
    <React.Fragment>
      <div className="page-content">
        {canAdd && (
          <CreateModalForm
            isOpen={modalCreate}
            toggle={toggleModalCreate}
            setShowAlertSuccess={setShowAlertSuccess}
            setMsgSuccess={setMsgSuccess}
          />
        )}
        {canEdit && branchId && (
          <EditModalForm
            isOpen={modalEdit}
            toggle={toggleModalEdit}
            branchId={branchId}
            setShowAlertSuccess={setShowAlertSuccess}
            setMsgSuccess={setMsgSuccess}
          />
        )}
        <Container fluid>
          <Alert color="danger" isOpen={branches.isError}>
            {branches.error?.errors?.map((error) => error?.message)}
          </Alert>

          <h2>Lista de Sucursales</h2>
          <div className="bg-white p-3">
            <ListHeader
              canAdd={canAdd}
              handleOnChangeSearch={handleChangeSearch}
              handleSearch={handleSearch}
              searchTxt={searchTxt}
              toggleModalCreate={toggleModalCreate}
              isComingSoonButton={true}
            />
            {!branches.error && isLoading ? (
              <div className="my-5 text-center">
                <Loading isLoading={isLoading} size={2} />
              </div>
            ) : branches.error && !branches.isLoading ? (
              <div className="my-5 text-center">
                <h1>Error intenta mas tarde</h1>
              </div>
            ) : (
              <div className="table-responsive" style={{ marginTop: "30px" }}>
                <Table>
                  <thead>
                    <tr>
                      <th style={styleCenterTable}>Nombre</th>
                      <th style={styleCenterTable}>Ubicación</th>
                      <th style={styleCenterTable}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {branches.data?.results.length > 0 ? (
                      branches.data?.results.map((item) => {
                        return (
                          <BranchesItemList
                            key={item.id}
                            item={item}
                            canDelete={canDelete}
                            canEdit={canEdit}
                            openDeleteModal={openDeleteModal}
                            openEditModal={openEditModal}
                          />
                        )
                      })
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center">
                          No hay datos
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>

                <div className="text-center">
                  {(branches.data?.next || branches.data?.previous) && (
                    <Pagination aria-label="Page navigation" className="d-flex justify-content-center">
                      {branches.data?.previous && (
                        <PaginationItem>
                          <PaginationLink
                            previous
                            onClick={() => handleSearch(branches.data?.previous.split("?")[1])}
                          />
                        </PaginationItem>
                      )}
                      {branches.data?.next && (
                        <PaginationItem>
                          <PaginationLink next onClick={() => handleSearch(branches.data?.next.split("?")[1])} />
                        </PaginationItem>
                      )}
                    </Pagination>
                  )}
                </div>
              </div>
            )}
          </div>
        </Container>
      </div>
      <Shortcuts toggleModalCreate={toggleModalCreate} />
    </React.Fragment>
  )
}

export default BranchesList
